import { Alert, Button, HeaderBlock, Prompt } from '@components'
import { BlueprintType } from '@constants'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { DocumentFormModal } from '@pages/DocumentPage/DocumentForm'
import {
  collectFromPages,
  deleteDocument,
  fetchDocument,
  flagDocument,
  IDocument,
  invalidateDashboardQueries,
  lockDocument,
  setAttachmentExempt,
  unflagDocument,
  unlockDocument,
  useInvalidateDocumentLimits
} from '@query'
import { AttachmentExemptIcon } from '@pages/DocumentPage/DocumentForm/AttachmentExemptIcon'
import React, { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaFlag, FaLock, FaLockOpen, FaPaperclip, FaPen, FaTrash } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { DocumentOverview } from './DocumentOverview'
import { queryKeyForAuditTrail } from './DocumentAuditTrail'
import { AlgorithmStatusBadge } from '@pages/DocumentPage/DocumentDetails/AccountingSuggestions/AlgorithmStatusBadge.tsx'

interface DocumentDetailsProps {
  documentId: number
  onClose: () => void
}

export const DocumentDetails: React.FC<DocumentDetailsProps> = ({ documentId, onClose }) => {
  const [t] = useTranslation()
  const [showEdit, setShowEdit] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const queryClient = useQueryClient()
  const invalidateDocumentLimits = useInvalidateDocumentLimits()
  const hasEditPermission = usePermission(PermissionSet.BOOKKEEPING_EDIT)
  const hasLockPermission = usePermission(PermissionSet.BOOKKEEPING_LOCK)
  const { slug } = useBusinessContext()
  const { data } = useQuery({
    queryKey: [slug, 'documents', documentId],
    queryFn: () => fetchDocument({ documentId, slug }),
    ...{
      onError: () => onClose(),
      initialData: () => {
        const queries = queryClient.getQueriesData({
          queryKey: [slug, 'documents']
        })
        for (const [, queryData] of queries) {
          if (queryData) {
            const documents = collectFromPages<IDocument>(queryData as any)
            const match = documents.find(d => d.id === documentId)
            if (match) return match
          }
        }
      }
    }
  })
  const document = data
  const onUpdateSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: [slug, 'documents'] })
    await queryClient.invalidateQueries({ queryKey: queryKeyForAuditTrail(slug, documentId) })
    await invalidateDashboardQueries(queryClient, slug)
  }

  const deleteMutation = useMutation({
    mutationFn: () => deleteDocument({ slug, documentId }),
    ...{
      onSuccess: async () => {
        onClose()
        setShowDelete(false)

        invalidateDocumentLimits(document?.date)
        await queryClient.removeQueries({ queryKey: [slug, 'documents', documentId] })
        await queryClient.removeQueries({ queryKey: [slug, 'documents', documentId, 'entries'] })
        await queryClient.invalidateQueries({ queryKey: [slug, 'documents'] })
        await invalidateDashboardQueries(queryClient, slug)

        toast.success(t('document.deletionSuccess'))
      }
    }
  })

  const flagMutation = useMutation({
    mutationFn: () => flagDocument({ slug, documentId }),
    ...{
      onSuccess: onUpdateSuccess
    }
  })

  const unflagMutation = useMutation({
    mutationFn: () => unflagDocument({ slug, documentId }),
    ...{
      onSuccess: onUpdateSuccess
    }
  })

  const toggleFlag = () => {
    if (!document) return
    const isFlagged = document.is_flagged
    const promise = isFlagged ? unflagMutation.mutateAsync() : flagMutation.mutateAsync()
    toast.promise(promise, {
      loading: isFlagged ? t('document.flaggingProgressUnset') : t('document.flaggingProgressSet'),
      success: isFlagged ? t('document.flaggingUnset') : t('document.flaggingSet'),
      error: t('error.oho')
    })
  }

  const lockMutation = useMutation({
    mutationFn: () => lockDocument({ slug, documentId }),
    ...{
      onSuccess: onUpdateSuccess
    }
  })

  const unlockMutation = useMutation({
    mutationFn: () => unlockDocument({ slug, documentId }),
    ...{
      onSuccess: onUpdateSuccess
    }
  })

  const attachmentExemptMutation = useMutation({
    mutationFn: (attachmentExempt: boolean) =>
      setAttachmentExempt({ slug, documentId }, attachmentExempt),
    onSuccess: onUpdateSuccess
  })

  const toggleAttachmentExempt = () => {
    if (!document) return
    const nextValue = !document.attachment_exempt
    toast.promise(attachmentExemptMutation.mutateAsync(nextValue), {
      loading: t('general.saving'),
      success: t('general.changesSaved'),
      error: t('error.oho')
    })
  }

  const menuItems = useMemo(
    () =>
      document
        ? [
            ...(hasLockPermission
              ? [
                  {
                    name: document.is_locked ? t('document.unlock') : t('document.lock'),
                    Icon: document.is_locked ? FaLockOpen : FaLock,
                    onClick: () =>
                      document.is_locked ? unlockMutation.mutateAsync() : lockMutation.mutateAsync()
                  }
                ]
              : []),
            ...(document.is_locked
              ? []
              : [
                  ...(hasEditPermission
                    ? [
                        {
                          name: document.is_flagged ? t('document.unflag') : t('document.flag'),
                          Icon: FaFlag,
                          onClick: () => toggleFlag()
                        },
                        {
                          name: document.attachment_exempt
                            ? t('document.requireAttachments')
                            : t('document.attachmentExempt'),
                          Icon: document.attachment_exempt ? FaPaperclip : AttachmentExemptIcon,
                          onClick: () => toggleAttachmentExempt(),
                          disabled: attachmentExemptMutation.isPending
                        },
                        {
                          name: t('general.delete'),
                          Icon: FaTrash,
                          onClick: () => setShowDelete(true),
                          intent: 'danger'
                        }
                      ]
                    : [])
                ])
          ]
        : [],
    [document, hasLockPermission, hasEditPermission, attachmentExemptMutation.isPending, t]
  )

  const header = useMemo(() => {
    if (document?.is_draft) return t('document.type.draft')
    if (document?.blueprint_type === BlueprintType.PURCHASE) return t('document.type.purchase')
    if (document?.blueprint_type === BlueprintType.SALES) return t('document.type.sales')
    return t('document.type.other')
  }, [document?.blueprint_type])

  const isDeletable = document?.is_deletable !== false

  if (!document) return null

  return (
    <>
      <StyledWrapper key={`document-${document.id}`}>
        <HeaderBlock
          header={header}
          subHeader={<AlgorithmStatusBadge document={document} />}
          menuItems={menuItems}
        />

        <ScrollableContent id={'document-tabs'}>
          <DocumentOverview document={document} setShowEdit={value => setShowEdit(value)} />
        </ScrollableContent>

        {!document.is_draft && !document.is_locked && hasEditPermission && (
          <StyledFooter>
            <Button
              icon={<FaPen />}
              onClick={() => setShowEdit(true)}
              data-test="document-edit-button"
            >
              {t('general.edit')}
            </Button>
          </StyledFooter>
        )}
      </StyledWrapper>

      <DocumentFormModal
        isVisible={showEdit}
        onClose={() => setShowEdit(false)}
        documentId={document.id}
      />

      <Prompt
        isVisible={showDelete}
        title={t('document.deleteTitle')}
        description={
          <DeletePromptContent>
            <div>{t('document.deleteDescription')}</div>
            <Alert
              type="error"
              title={t('document.cannotDelete')}
              isVisible={!isDeletable}
              description={t('document.cannotDeleteDescription')}
            />
          </DeletePromptContent>
        }
        onClose={() => setShowDelete(false)}
        buttons={[
          {
            text: t('general.cancel'),
            intent: 'default',
            action: () => setShowDelete(false),
            disabled: deleteMutation.isPending
          },
          {
            text: t('general.delete'),
            icon: <FaTrash />,
            intent: 'danger',
            action: async () => {
              try {
                await deleteMutation.mutateAsync()
              } catch {
                return
              }
            },
            disabled: !isDeletable || deleteMutation.isPending,
            showSpinner: deleteMutation.isPending
          }
        ]}
      />
    </>
  )
}

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  background: white;
  display: flex;
  flex-direction: column;
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing.md}rem;
  padding-top: 0;
`

const StyledFooter = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing.md}rem;
  justify-content: space-between;
`

const DeletePromptContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
